import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LogIn, Loader2 } from 'lucide-react';
import { authAPI } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

export const AdminLogin = () => {
  const { toast } = useToast();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.login(credentials.email, credentials.password);
      toast({
        title: 'Login Successful',
        description: 'Welcome back to Kaya Property Management',
      });
      setTimeout(() => {
        window.location.href = '/admin/dashboard';
      }, 500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid email or password';
      toast({
        title: 'Login Failed',
        description: typeof detail === 'string' ? detail : 'An error occurred',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#C5A05E]/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `
            linear-gradient(rgba(197, 160, 94, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(197, 160, 94, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] opacity-50 blur-2xl rounded-3xl" />
        
        <Card className="relative backdrop-blur-2xl bg-white/5 border border-white/10 shadow-2xl">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="flex justify-center">
              <div className="w-24 h-24 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 flex items-center justify-center p-3">
                <img
                  src="/logo.png"
                  alt="Kaya Property Management"
                  className="h-full w-auto object-contain"
                />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-white" data-testid="admin-login-title">Admin Login</CardTitle>
            <p className="text-gray-400">Sign in to access the dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
              <div>
                <label className="block text-sm font-semibold text-white mb-3">Email</label>
                <Input
                  type="email"
                  required
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  placeholder="admin@kaya-group.eu"
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] focus:ring-[#C5A05E]/30 rounded-xl py-6"
                  data-testid="admin-login-email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-3">Password</label>
                <Input
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] focus:ring-[#C5A05E]/30 rounded-xl py-6"
                  data-testid="admin-login-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="relative overflow-hidden w-full bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-bold shadow-2xl shadow-[#C5A05E]/50 transition-all duration-500 hover:scale-105 hover:shadow-[#C5A05E]/70 group py-7 rounded-2xl border-2 border-[#C5A05E]/50 disabled:opacity-70 disabled:cursor-not-allowed"
                size="lg"
                data-testid="admin-login-submit"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Signing in...</>
                ) : (
                  <><LogIn className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" /> Sign In</>
                )}
              </Button>

              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => (window.location.href = '/')}
                  className="text-sm text-gray-400 hover:text-[#C5A05E] transition-colors"
                >
                  ← Back to Website
                </button>
              </div>

              {/* Demo Credentials */}
              <div className="mt-6 p-4 backdrop-blur-xl bg-[#C5A05E]/10 rounded-xl border border-[#C5A05E]/20">
                <p className="text-xs text-[#C5A05E] font-semibold mb-2 uppercase tracking-wider">Demo Credentials</p>
                <p className="text-xs text-gray-300">Email: admin@kaya-group.eu</p>
                <p className="text-xs text-gray-300">Password: admin123</p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      <Toaster />
    </div>
  );
};
